import { Controller } from '@hotwired/stimulus'

// P1-9 FIX: Debug logging helper - only logs in development or when debug flag is set
const DEBUG = typeof window !== 'undefined' && (window.PAGE_EDITOR_DEBUG || window.location?.hostname === 'localhost')
const debugLog = (...args) => DEBUG && console.log(...args)

/**
 * PagePresenceController - Stimulus controller for Phase 4 Collaboration
 *
 * Mounts PresenceAvatars Vue component and manages PresenceManager lifecycle.
 * Requires actionCableConsumer to be available globally (set by setupActionCable).
 *
 * P1-6 FIX: Uses reactive presences ref instead of remounting Vue on every update.
 */
export default class extends Controller {
  static targets = ['avatars']

  static values = {
    pageId: String,
    adminId: String
  }

  connect() {
    // P1-4 FIX: Use debugLog instead of console.log for happy path logging
    debugLog('[PagePresenceController] Connected, page:', this.pageIdValue, 'admin:', this.adminIdValue)

    // Delay initialization to ensure ActionCable consumer is ready
    setTimeout(() => {
      this.initializePresence()
    }, 100)
  }

  disconnect() {
    debugLog('[PagePresenceController] Disconnecting')

    // Cleanup presence manager subscription
    if (this.unsubscribePresence) {
      this.unsubscribePresence()
      this.unsubscribePresence = null
    }

    // Cleanup presence manager
    if (this.presenceManager) {
      this.presenceManager.disconnect()
      this.presenceManager = null
    }

    // Unmount Vue
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }

    // Clear reactive ref
    this.presencesRef = null
  }

  async initializePresence(attempt = 1) {
    const maxAttempts = 25 // Up to 5 seconds total
    const { PresenceManager } = window.PageEditor || {}

    // P1-4 FIX: Use debugLog for happy path, console.error for failures
    debugLog('[PagePresenceController] initializePresence attempt', attempt, 'PresenceManager:', !!PresenceManager)

    if (!PresenceManager) {
      if (attempt < maxAttempts) {
        setTimeout(() => this.initializePresence(attempt + 1), 200)
      } else {
        console.error('[PagePresenceController] PresenceManager not available after', maxAttempts, 'attempts')
      }
      return
    }

    // Get shared ActionCable consumer (set by toolbar setupActionCable)
    const consumer = window.actionCableConsumer
    debugLog('[PagePresenceController] ActionCable consumer:', !!consumer)
    if (!consumer) {
      if (attempt < maxAttempts) {
        setTimeout(() => this.initializePresence(attempt + 1), 200)
      } else {
        console.error('[PagePresenceController] ActionCable consumer not available after', maxAttempts, 'attempts')
      }
      return
    }

    // Create presence manager
    debugLog('[PagePresenceController] Creating PresenceManager for page:', this.pageIdValue)
    this.presenceManager = new PresenceManager({
      pageId: this.pageIdValue,
      currentAdminId: this.adminIdValue,
      consumer
    })

    try {
      // Connect to channel
      debugLog('[PagePresenceController] Connecting to channel...')
      await this.presenceManager.connect()
      debugLog('[PagePresenceController] PresenceManager connected!')

      // Expose presence manager globally for toolbar integration
      window.pagePresenceManager = this.presenceManager

      // Mount Vue component (P1-6 FIX: mounts once with reactive ref)
      debugLog('[PagePresenceController] Mounting Vue component...')
      await this.mountVueComponent()
      debugLog('[PagePresenceController] Vue component mounted, presences:', this.presenceManager?.getAllPresences())

      // Subscribe to presence changes - updates reactive ref instead of remounting
      this.unsubscribePresence = this.presenceManager.onPresenceChange((presences) => {
        debugLog('[PagePresenceController] Presence change:', presences.length, 'users')
        this.updatePresences(presences)
      })
    } catch (err) {
      console.error('[PagePresenceController] Connection failed:', err)
    }
  }

  async mountVueComponent() {
    if (!this.hasAvatarsTarget) {
      console.warn('[PagePresenceController] No avatars target found')
      return
    }

    try {
      const [{ createApp, ref }, { default: PresenceAvatars }] = await Promise.all([
        import('vue'),
        import('~/javascript/components/admin/page_editor/PresenceAvatars.vue')
      ])

      // P1-6 FIX: Create reactive ref for presences that can be updated without remounting
      this.presencesRef = ref(this.presenceManager?.getAllPresences() || [])

      // Create a wrapper component that uses the reactive ref
      const WrapperComponent = {
        setup: () => ({
          presences: this.presencesRef,
          maxVisible: 4,
          onScrollToBlock: (blockId) => this.scrollToBlock(blockId)
        }),
        render() {
          return this.$h(PresenceAvatars, {
            presences: this.presences,
            maxVisible: this.maxVisible,
            onScrollToBlock: this.onScrollToBlock
          })
        }
      }

      // Create Vue app with wrapper
      this.vueApp = createApp(WrapperComponent)

      // Provide h function to wrapper
      this.vueApp.config.globalProperties.$h = (await import('vue')).h

      this.vueApp.mount(this.avatarsTarget)
      debugLog('[PagePresenceController] Vue component mounted with reactive presences')
    } catch (err) {
      console.error('[PagePresenceController] Vue mount failed:', err)
    }
  }

  // P1-6 FIX: Update reactive ref instead of remounting Vue component
  updatePresences(presences) {
    if (this.presencesRef) {
      this.presencesRef.value = presences
    }
  }

  scrollToBlock(blockId) {
    debugLog('[PagePresenceController] Scrolling to block:', blockId)

    // Find block in iframe or main document
    const previewFrame = document.getElementById('previewFrame')
    const docs = [document]
    if (previewFrame?.contentDocument) {
      docs.push(previewFrame.contentDocument)
    }

    for (const doc of docs) {
      const block = doc.querySelector(`[data-block-id="${blockId}"]`)
      if (block) {
        block.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // P1-4 FIX: Use CSS class for scroll highlight instead of inline style
        // This matches the approach used in the toolbar's scrollToBlock()
        block.classList.remove('block-scroll-highlight')
        // Force reflow to restart animation (assign to discard variable)
        // eslint-disable-next-line no-unused-expressions
        block.offsetWidth
        block.classList.add('block-scroll-highlight')
        // Clean up class after animation completes (1.5s)
        setTimeout(() => {
          block.classList.remove('block-scroll-highlight')
        }, 1500)

        return
      }
    }
  }
}
